"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { FileSearch } from "lucide-react";

const EVENTS = [
  { t: "11:32:04", who: "MARIA GONZÁLEZ", action: { en: "Checked in", es: "Check-in" }, by: "@door-east", tier: "GA" },
  { t: "11:32:09", who: "DAVID ORTIZ", action: { en: "Checked in", es: "Check-in" }, by: "@door-main", tier: "VIP" },
  { t: "11:32:14", who: "SARAH KIM", action: { en: "Denied · duplicate", es: "Negado · duplicado" }, by: "@door-main", tier: "GA", deny: true },
  { t: "11:32:18", who: "MARCO AURELIO", action: { en: "Walk-up added", es: "Walk-up agregado" }, by: "@host-jose", tier: "GA+1", walkup: true },
  { t: "11:32:22", who: "ALEX RIVERA", action: { en: "Checked in", es: "Check-in" }, by: "@door-east", tier: "GA" },
];

export default function QrDoorAuditScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Audit log", "Log de auditoría")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("Every scan ", "Cada scan ")}
            <span className="text-white/30">{t("writes itself.", "se registra solo.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "Who checked in, who scanned, which door, exact second. Duplicates and denies logged too. Next morning: review the line, find the lost ticket, close out the night.",
              "Quién entró, quién escaneó, qué puerta, segundo exacto. Duplicados y denies también logged. Al día siguiente: revisas la fila, encuentras el ticket perdido, cierras la noche."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[24px] p-5 md:p-6 max-w-[820px] mx-auto"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <FileSearch className="w-4 h-4" style={{ color: "#C4B5FD" }} />
              <div className="text-[10px] font-black tracking-widest uppercase text-white/55">
                {t("Audit log · NEON RUSH", "Log · NEON RUSH")}
              </div>
            </div>
            <div className="text-[10px] font-bold text-white/45 tabular-nums">
              {t("Filtered: 11:32 PM window", "Filtrado: ventana 11:32 PM")}
            </div>
          </div>

          {/* Audit lines */}
          <div className="space-y-1.5" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {EVENTS.map((e, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.35, delay: 0.15 + 0.08 * i }}
                className={`flex items-center gap-3 rounded-[10px] px-3 py-2 ${
                  e.deny ? "border" : e.walkup ? "border" : "bg-white/[0.03] border border-white/5"
                }`}
                style={
                  e.deny
                    ? {
                        backgroundColor: "rgba(220,38,38,0.10)",
                        borderColor: "rgba(220,38,38,0.30)",
                      }
                    : e.walkup
                      ? {
                          backgroundColor: "rgba(251,146,60,0.10)",
                          borderColor: "rgba(251,146,60,0.30)",
                        }
                      : undefined
                }
              >
                <span className="text-[10px] text-white/45 tabular-nums shrink-0">{e.t}</span>
                <span className="text-[11px] font-black text-white truncate min-w-0 flex-1">{e.who}</span>
                <span
                  className="text-[10px] font-bold shrink-0"
                  style={{
                    color: e.deny ? "#DC2626" : e.walkup ? "#FB923C" : "#2ECC71",
                  }}
                >
                  {t(e.action.en, e.action.es)}
                </span>
                <span className="text-[9px] text-white/45 hidden sm:inline shrink-0">{e.by}</span>
                <span
                  className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
                  style={{
                    backgroundColor: e.tier.startsWith("VIP")
                      ? "rgba(244,114,182,0.18)"
                      : "rgba(255,255,255,0.06)",
                    color: e.tier.startsWith("VIP") ? "#F472B6" : "rgba(255,255,255,0.55)",
                  }}
                >
                  {e.tier}
                </span>
              </motion.div>
            ))}
          </div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[10px] font-medium"
          >
            <span className="flex items-center gap-1.5 text-white/55">
              <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: "#2ECC71" }} />
              {t("Checked in", "Check-in")}
            </span>
            <span className="flex items-center gap-1.5 text-white/55">
              <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: "#FB923C" }} />
              {t("Walk-up", "Walk-up")}
            </span>
            <span className="flex items-center gap-1.5 text-white/55">
              <span className="w-1.5 h-1.5 rounded-sm" style={{ backgroundColor: "#DC2626" }} />
              {t("Denied", "Negado")}
            </span>
            <span className="ml-auto text-white/45 tabular-nums">
              {t("487 events tonight", "487 eventos esta noche")}
            </span>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
